import { UpdateTransactionDto } from "@common/dto/transactions/update-transaction.dto";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import classNames from "classnames";
import { Form, Formik } from "formik";
import { memo, useCallback, useContext, useMemo } from "react";
import { z } from "zod";
import { useTransactionViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useUser } from "@hooks/useUser";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@design-system/Popover/Popover";
import { addTransactionFieldReviews } from "@api/transaction.api";
import {
    NoteFormSchema,
    useNoteEditing,
} from "@components/organizationView/clients/views/cells/useNoteEditing";
import { useNotePopoverClose } from "@components/organizationView/clients/views/cells/useNotePopoverClose";
import { NoteTextarea } from "@components/organizationView/clients/views/cells/NoteTextarea";
import { PaginatedTransactionsActionsContext } from "../PaginatedTransactionsTable.context";
import { updateTransactionInCache } from "../useTransactionsQuery";
import {
    isTransactionFieldReviewedByUser,
    mergeTransactionFieldReviews,
} from "./transactionFieldReviewUtils";
import { TransactionFieldReviewButton } from "./TransactionFieldReviewButton";
import { InlineNoteCell } from "./InlineNoteCell";
import { NoteCellVariant } from "./noteCellVariants";
import { useRegisterNoteCellOpener } from "./useRegisterNoteCellOpener";
import styles from "./NoteCell.module.scss";

type NoteCellProps = CellContext<
    ViewTableFeatures,
    TransactionListItem,
    unknown
>;

const NoteCellVariantSchema = z.nativeEnum(NoteCellVariant);

type NoteCellVariantValue = z.infer<typeof NoteCellVariantSchema>;

// Reads `customVariants` only. `resolveColumnVariant` also honors the legacy
// top-level `variant`, which would flip older saved Memo columns to review.
const NoteCellMetaSchema = z
    .object({ customVariants: z.object({ variant: NoteCellVariantSchema }) })
    .transform(({ customVariants }) => customVariants.variant)
    .catch(NoteCellVariant.DEFAULT);

const PopoverNoteCell = ({
    row,
    table,
    variant,
}: Pick<NoteCellProps, "row" | "table"> & {
    variant: NoteCellVariantValue;
}) => {
    const user = useUser();
    const { saveTransaction } = useContext(PaginatedTransactionsActionsContext);
    const note = row.original.memo;
    const { options: tableOptions } = useTransactionViewTableMeta(table);
    const isReadonly = tableOptions?.readonly ?? false;

    const isReviewVariant = variant === NoteCellVariant.REVIEW;

    const isReviewed = useMemo(
        () =>
            isTransactionFieldReviewedByUser(
                row.original.fieldReviews,
                TransactionReviewField.NOTE,
                user?.id,
            ),
        [row.original.fieldReviews, user?.id],
    );

    const handleUpdate = useCallback(
        async (noteValue: string) => {
            const payload: UpdateTransactionDto = { memo: noteValue };
            await saveTransaction(row.original, payload);
            if (isReviewVariant && noteValue !== "") {
                const reviews = await addTransactionFieldReviews(
                    row.original.id,
                    [TransactionReviewField.NOTE],
                );
                updateTransactionInCache(row.original.id, (t) => ({
                    ...t,
                    fieldReviews: mergeTransactionFieldReviews(
                        t.fieldReviews,
                        reviews,
                    ),
                }));
            }
        },
        [isReviewVariant, saveTransaction, row.original],
    );

    const { closeAndBlur, setClose } = useNotePopoverClose();

    const {
        isOpened,
        setIsOpened,
        textareaRef,
        formRef,
        handleSubmit,
        handleBlur,
        handleFocus,
        handleKeyDown,
    } = useNoteEditing(note, handleUpdate, closeAndBlur);

    setClose(() => setIsOpened(false));

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (open && isReadonly) {
                return;
            }
            setIsOpened(open);
        },
        [isReadonly, setIsOpened],
    );

    const openPopover = useCallback(() => {
        if (!isReadonly) {
            setIsOpened(true);
        }
    }, [isReadonly, setIsOpened]);
    useRegisterNoteCellOpener({
        table,
        rowId: row.original.id,
        open: openPopover,
    });

    return (
        <ViewTableCell captureClicks className={styles.container}>
            <Formik<NoteFormSchema>
                initialValues={{ value: note ?? "" }}
                onSubmit={handleSubmit}
                enableReinitialize
                innerRef={formRef}
            >
                <Popover open={isOpened} onOpenChange={handleOpenChange}>
                    <PopoverTrigger>
                        <EditableCellContent
                            readonly={isReadonly}
                            isEmpty={!note}
                            truncationFullText={note ?? undefined}
                            className={classNames({
                                [styles.reviewVariant]: isReviewVariant,
                                [styles.isReviewed]: isReviewed,
                            })}
                        >
                            {note}
                        </EditableCellContent>
                    </PopoverTrigger>
                    <PopoverContent
                        side="bottom"
                        align="start"
                        sideOffset={-32}
                        className={styles.popover}
                    >
                        <Form>
                            <NoteTextarea
                                textareaRef={textareaRef}
                                handleFocus={handleFocus}
                                handleBlur={handleBlur}
                                handleKeyDown={handleKeyDown}
                                noteTextareaClass={styles.noteTextarea}
                            />
                        </Form>
                    </PopoverContent>
                </Popover>
            </Formik>

            {isReviewVariant && (
                <TransactionFieldReviewButton
                    transaction={row.original}
                    field={TransactionReviewField.NOTE}
                    reviewTooltip="Review note"
                    isReadonly={isReadonly}
                />
            )}
        </ViewTableCell>
    );
};

/**
 * Memo column cell. The `inline` column variant renders an always-visible
 * input; every other variant edits through a popover.
 */
export const NoteCell = memo(({ row, table, column }: NoteCellProps) => {
    const variant = NoteCellMetaSchema.parse(column.columnDef.meta);

    if (variant === NoteCellVariant.INLINE) {
        return <InlineNoteCell row={row} table={table} />;
    }

    return <PopoverNoteCell row={row} table={table} variant={variant} />;
});
NoteCell.displayName = "NoteCell";
